import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { DeTherapyPrescriptionDrawer } from "@/modules/patients/detail/therapy-prescription/de/de-therapy-prescription-drawer"
import { deTherapyPrescriptionFragment$key } from "@/modules/patients/detail/therapy-prescription/de/services/__generated__/deTherapyPrescriptionFragment.graphql"
import { deTherapyPrescriptionFragment } from "@/modules/patients/detail/therapy-prescription/de/services/deTherapyPrescriptionFragment"
import { useTherapyDrawerStoreContext } from "@/modules/patients/detail/therapy-prescription/store/useTherapyDrawerStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface TherapyPrescriptionDocumentProps extends DocumentProps {
  therapyPrescriptionRef: deTherapyPrescriptionFragment$key
  progressNoteId: string
  isEditable?: boolean
}

export const PhysicalTherapyIcon = ({ isSelected }: { isSelected?: boolean }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className="h-6 w-6 shrink-0">
      <path
        d="M14.1269 20.886L9.81925 19.1922H4.40387C4.14779 19.1922 3.93313 19.1056 3.7599 18.9323C3.58665 18.7591 3.50002 18.5444 3.50002 18.2883V15.6922C3.50002 14.4435 3.93784 13.3813 4.81347 12.5057C5.68912 11.6301 6.7513 11.1923 8 11.1923H18.725C19.2191 11.1923 19.6385 11.3608 19.9831 11.6978C20.3277 12.0348 20.5 12.4496 20.5 12.9422C20.5 13.3499 20.3769 13.7114 20.1308 14.0268C19.8846 14.3422 19.5711 14.5537 19.1904 14.6614L16.5961 15.3845V19.2114C16.5961 19.5288 16.5248 19.8159 16.3821 20.0728C16.2394 20.3297 16.048 20.5413 15.8077 20.7076C15.5657 20.8733 15.301 20.9749 15.0137 21.0126C14.7263 21.0503 14.4307 21.008 14.1269 20.886ZM12.2004 16.1922H9.27885C9.1173 16.1922 8.98973 16.2415 8.89615 16.3403C8.80255 16.439 8.74421 16.5492 8.72113 16.671C8.69806 16.7928 8.71537 16.9137 8.77305 17.0336C8.83075 17.1534 8.93524 17.2396 9.08652 17.2922L14.6731 19.4999C14.7756 19.5383 14.8718 19.5271 14.9615 19.4662C15.0513 19.4053 15.0962 19.3204 15.0962 19.2114V16.1922H12.2004ZM5 17.6922H7.64812C7.51862 17.573 7.41349 17.434 7.33272 17.2753C7.25194 17.1166 7.21155 16.9447 7.21155 16.7595C7.21155 16.1937 7.41465 15.7077 7.82085 15.3015C8.22707 14.8953 8.71307 14.6922 9.27885 14.6922H13.2962L18.8 13.2172C18.8833 13.1839 18.9417 13.1422 18.975 13.0922C19.0083 13.0422 19.0167 12.9839 19 12.9172C18.9833 12.8505 18.9542 12.7964 18.9125 12.7547C18.8708 12.713 18.8083 12.6922 18.725 12.6922H8C7.16667 12.6922 6.45833 12.9839 5.875 13.5672C5.29167 14.1505 5 14.8589 5 15.6922V17.6922ZM10 9.99993C9.03752 9.99993 8.21357 9.65722 7.52815 8.9718C6.84273 8.2864 6.50003 7.46245 6.50003 6.49995C6.50003 5.53747 6.84273 4.71353 7.52815 4.02813C8.21357 3.34271 9.03752 3 10 3C10.9625 3 11.7864 3.34271 12.4719 4.02813C13.1573 4.71353 13.5 5.53747 13.5 6.49995C13.5 7.46245 13.1573 8.2864 12.4719 8.9718C11.7864 9.65722 10.9625 9.99993 10 9.99993ZM10 8.49995C10.55 8.49995 11.0208 8.30412 11.4125 7.91245C11.8042 7.52078 12 7.04995 12 6.49995C12 5.94995 11.8042 5.47912 11.4125 5.08745C11.0208 4.69578 10.55 4.49995 10 4.49995C9.45 4.49995 8.97917 4.69578 8.5875 5.08745C8.19583 5.47912 8 5.94995 8 6.49995C8 7.04995 8.19583 7.52078 8.5875 7.91245C8.97917 8.30412 9.45 8.49995 10 8.49995Z"
        className={isSelected ? "fill-on-primary-surface" : "fill-on-surface-subdued"}
      />
    </svg>
  )
}

const TherapyPrescriptionDocument = ({
  therapyPrescriptionRef,
  progressNoteId,
  isEditable = true,
  isConclusion,
  isSigned,
  onSelect,
  isSent,
}: TherapyPrescriptionDocumentProps) => {
  const { t } = useTranslation()
  const { id, treatmentUnit, frequency } = useFragment(deTherapyPrescriptionFragment, therapyPrescriptionRef) ?? {}
  const setTherapyPrescription = useTherapyDrawerStoreContext((state) => state.setTherapyPrescription)
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = !isEditable && selectedDocumentId === id

  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered relative flex items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="therapy-prescription-document-item"
      onClick={() => {
        if (!isEditable) {
          onSelect()
          return
        }
        openDrawer(() => <DeTherapyPrescriptionDrawer progressNoteId={progressNoteId} />, "TherapyPrescriptionDrawer")
        setTherapyPrescription(id, therapyPrescriptionRef)
      }}>
      <PhysicalTherapyIcon isSelected={isSelected} />
      <div className="ml-2 min-w-0">
        <ListText className={isSelected ? "text-on-primary-surface" : ""}>
          {t("episodeOfCare.progressNote.deTherapyPrescription")}
        </ListText>
        <ListText
          data-testid="therapy-prescription-details"
          variant="secondary"
          className={cn("truncate", isSelected ? "text-on-primary-surface" : "")}>
          {`${t("patient.therapyPrescription.units")}: ${treatmentUnit} • ${t(
            "patient.therapyPrescription.frequency",
          )}: ${frequency}`}
        </ListText>
      </div>
      {isEditable && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="therapy-prescription-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="absolute right-2 ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="therapy-prescription-view-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { TherapyPrescriptionDocument }
